import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  Grid,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from '../../../components/snackbar';
import { LeadTraceMatrix } from 'api/reports';
// export default function LeadTraceMatrixSection({
//   leadKeys = [],
//   metrices = [],
//   notNormalMetrices = [],
//   initialValues = {},
//   onChange = () => { },
//   ecgReportData = {},
//   inputFormData = {},
//   isLTData,
//   leadReplica
// }) {
const LeadTraceMatrixSection = forwardRef(({
  leadKeys = [],
  metrices = [],
  metricesLabels = [],
  notNormalMetrices = [],
  initialValues = {},
  onChange = () => { },
  ecgReportData = {},
  inputFormData = {},
  isLTData,
  leadReplica
}, ref) => {
  const [formData, setFormData] = useState(initialValues);
  const [saving, setSaving] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const LEAD_LABELS = {
    lead1_data: 'L1',
    lead2_data: 'L2',
    lead3_data: 'L3',
    v1_data: 'V1',
    v2_data: 'V2',
    v3_data: 'V3',
    v4_data: 'V4',
    v5_data: 'V5',
    v6_data: 'V6',
    avr_data: 'AVR',
    avl_data: 'AVL',
    avf_data: 'AVF',
  };
  const LEAD_ORDER = [
    'lead1_data', 'lead2_data', 'lead3_data',
    'v1_data', 'v2_data', 'v3_data', 'v4_data', 'v5_data', 'v6_data', 'avr_data', 'avl_data', 'avf_data',
  ];
  const sortedLeadKeys = [...leadKeys].sort((a, b) => {
    return LEAD_ORDER.indexOf(a) - LEAD_ORDER.indexOf(b);
  });

  const otherMatrixDataMapping = () => {
    let otherMatrices = {
      r_peaks: [],
      lbbb: false,
      st_elevation: false,
      st_dipression: false,
      mi_all: false
    }
    if (ecgReportData) otherMatrices.r_peaks = ecgReportData?.characteristics?.r_peak_points ?? []
    // if (formInput?.classification_of_abnormality === "LBBB") otherMatrices.lbbb = true
    if (inputFormData?.classification_of_abnormality === "ST-T Changes") otherMatrices.st_elevation = true
    if (inputFormData?.classification_of_abnormality === "Myocardial Infarction") otherMatrices.mi_all = true
    if (inputFormData?.findings?.includes("ST- depression and T-wave inversion")) otherMatrices.st_dipression = true

    return otherMatrices
  }

  const ecg_quality_matrix = {
    ...formData,
    ...otherMatrixDataMapping()
  };


  const handleMatrixChange = (leadKey, type) => (e) => {
    const isNotNormal = notNormalMetrices?.includes(type)
    const updated = {
      ...formData,
      lead_trace_quality: {
        ...formData.lead_trace_quality,
        [leadKey]: {
          ...formData.lead_trace_quality[leadKey],
          [type]: e.target.checked,
          ...(isNotNormal ? { "normal": false } : {})
        },
      },
    };
    setFormData(updated);
    onChange(updated);
  };
  const handleGlobalChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    const updated = { ...formData, [field]: value };
    setFormData(updated);
    onChange(updated);
  };

  const hasValidLeadSelections = () => {
    const ltq = formData.lead_trace_quality || {};

    // If no leads exist, block save
    if (!Object.keys(ltq).length) return false;

    // Each lead must have at least one checked option
    return Object.values(ltq).every((leadRow) =>
      Object.values(leadRow || {}).some(Boolean)
    );
  };

  useImperativeHandle(ref, () => ({
    hasValidLeadSelections
  }));


  const handleSubmit = async () => {
    // if (!hasAnySelection()) {
    //   enqueueSnackbar(
    //     "Please select at least one lead quality option before saving.",
    //     { variant: "warning" }
    //   );
    //   return;
    // }
    if (!hasValidLeadSelections()) {
      enqueueSnackbar(
        "Please select at least one option (Normal / Noise / Loose Electrodes etc.) for each LEAD before saving.",
        { variant: "warning" }
      );
      return;
    }

    try {
      setSaving(true);
      const interpretationWR = leadReplica ? true : false;
      const response = await LeadTraceMatrix({ reportId: ecgReportData?.id, ecg_quality_matrix: ecg_quality_matrix, status: "completed", interpretationWR });
      if (response) {
        enqueueSnackbar("Data saved successfully!", { variant: "success" });
        setSaving(false);
        onChange(formData);   // notify parent on success
      } else {
        enqueueSnackbar("Failed to save data.", { variant: "error" });
        setSaving(false);
      }
    } catch (err) {
      console.error("Error while saving:", err);
      enqueueSnackbar("An error occurred while saving.", { variant: "error" });
    }
  };
  return (
    <Box p={2}>
      {/* Matrix */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="span" fontWeight="bold" gutterBottom>
          Lead Trace Quality:
        </Typography>
        {
          !isLTData &&
          ecgReportData?.interpretation_data?.ecg_quality_matrix?.status !== "completed" &&
          ecgReportData?.interpretation_request_status === "completed" &&
          (
            <LoadingButton
              variant="contained"
              color="primary"
              size="small"
              loading={saving}
              onClick={handleSubmit}
            >
              Save Lead Quality
            </LoadingButton>
          )
        }



      </Box>
      {/* Global Flags */}
      <Box>
        <Box display="flex" flexDirection="column" gap={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!formData.lead_2_q_pathalogical_wave}
                onChange={handleGlobalChange('lead_2_q_pathalogical_wave')}
              />
            }
            label="Lead 2 Q-Pathological Wave"
          />
        </Box>
      </Box>

      {leadKeys?.length ? <Box mt={2} sx={{ maxWidth: '38vw', overflowX: 'auto' }}>
        <TableContainer component={Paper} sx={{ maxHeight: (leadKeys?.length === 1 ? 200 : 400), overflow: 'auto' }}>
          <Table stickyHeader size="small" aria-label="lead trace matrix">
            <TableHead>
              <TableRow>
                <TableCell sx={{ background: 'white', border: 'none', borderRight: '1px solid #E0E0E0' }}>&nbsp;</TableCell>
                <TableCell sx={{
                  backgroundColor: "#e6f4ea",
                  color: "#2e7d32",
                  textAlign: "center",
                  p: 0.5,
                  fontWeight: 600,
                }}>Acceptable</TableCell>
                <TableCell colSpan={6} sx={{
                  backgroundColor: "#f8d7da",
                  color: "#c62828",
                  textAlign: "center",
                  p: 0.5,
                  fontWeight: 600,
                }}>Unacceptable
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{
                  borderRight: '1px solid #E0E0E0',
                  position: 'sticky',
                  top: 0,
                  left: 0,
                  zIndex: 3,
                  background: 'white',
                  fontWeight: 'bold',
                }}></TableCell>
                {metrices.map((type) => (
                  <TableCell key={type} align="center"
                    sx={{
                      position: 'sticky',
                      top: 0,
                      left: 0,
                      zIndex: 3,
                      background: 'white',

                      // fontWeight: 'bold',
                    }}>
                    <strong>{metricesLabels[type] || type?.replace(/_/g, ' ')?.replace(/\b\w/g, char => char.toUpperCase())}</strong>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedLeadKeys.map((lead) => {
                const leadKey = `${lead}`;
                const leadData = formData.lead_trace_quality?.[leadKey] || {};
                return (
                  <TableRow key={lead}>
                    <TableCell
                      sx={{
                        width: '80px',
                        position: 'sticky',
                        left: 0,
                        background: 'white', // or your theme background
                        zIndex: 1, // ensure it stays above other cells
                        borderRight: '1px solid #E0E0E0',
                      }}
                    >{LEAD_LABELS[lead] || lead}</TableCell>
                    {metrices.map((type) => (
                      <TableCell key={type} align="center">
                        <Checkbox
                          checked={!!leadData[type]}
                          onChange={handleMatrixChange(leadKey, type)}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box> : null}
    </Box>
  );
}
)

export default LeadTraceMatrixSection;






